<ng-template #statusCellTemplate let-row>
  @if (isActiveLine(row)) {
    <span class="c-tag context:success">{{ 'portTariffLines.status.active' | translate }}</span>
  } @else {
    <span class="c-tag context:error">{{ 'portTariffLines.status.archived' | translate }}</span>
  }
</ng-template>

<ng-template #formulaPreviewCellTemplate let-row>
  <span class="c-tag">{{ tableLinePreview(row) }}</span>
</ng-template>

@if (tariffId() && tariff()) {
  <core-generic-table
    [dataInput]="tariffLines()"
    [columns]="lineColumns()"
    [actions]="[]"
    [customActions]="lineCustomActions()"
    [globalActions]="lineGlobalActions()"
    [showFilter]="false"
    [showSelection]="false"
    [showActions]="true"
    [showCreateButton]="false"
    [enablePagination]="false"
    [rowStyleConfigs]="lineRowStyleConfigs()"
    [listTitle]="'portTariffLines.header.fallbackTitle' | translate"
    class="o-layout__section c-table--scroll">
  </core-generic-table>
} @else {
  <div class="c-empty-state u-card">
    <p>{{ 'portTariffLines.emptyState' | translate }}</p>
  </div>
}

<ng-template #lineFormTemplate let-data let-updateField="updateField" let-mode="mode">
  @if (data) {
    <div class="ptl-form">
      <div
        coreDynamicField
        [field]="conceptFieldConfig()"
        [value]="data.concept"
        [mode]="mode"
        (valueChange)="updateField('concept', $event)">
      </div>

      <div class="ptl-form__field">
        <label class="ptl-form__label">{{ 'portTariffLines.fields.lineType' | translate }}</label>
        <div class="ptl-radio-group">
          <button
            type="button"
            class="ptl-radio-card"
            [class.is-selected]="data.lineType !== LineType.TAX"
            (click)="setLineType(updateField, LineType.STANDARD)">
            <span class="ptl-radio-dot" [class.is-selected]="data.lineType !== LineType.TAX"></span>
            {{ 'portTariffLines.options.lineType.standard' | translate }}
          </button>
          <button
            type="button"
            class="ptl-radio-card"
            [class.is-selected]="data.lineType === LineType.TAX"
            (click)="setLineType(updateField, LineType.TAX)">
            <span class="ptl-radio-dot" [class.is-selected]="data.lineType === LineType.TAX"></span>
            {{ 'portTariffLines.options.lineType.tax' | translate }}
          </button>
        </div>
      </div>

      <div
        coreDynamicField
        [field]="expenseCategoryFieldConfig()"
        [value]="data.expenseCategory"
        [mode]="mode"
        (valueChange)="onExpenseCategoryChange(updateField, $event)">
      </div>

      @if (needsOperationDirectionSelector(data.expenseCategory)) {
        <div
          coreDynamicField
          [field]="operationDirectionFieldConfig()"
          [value]="data.operationDirection"
          [mode]="mode"
          (valueChange)="updateField('operationDirection', $event)">
        </div>
      }

      <div
        coreDynamicField
        [field]="applicableVesselFieldConfig()"
        [value]="applicableVesselSelectValue(data.applicableVessel)"
        [mode]="mode"
        (valueChange)="onApplicableVesselChange(updateField, $event)">
      </div>

      <div class="ptl-accordion">
        <div class="ptl-accordion__section">
          <button type="button" class="ptl-accordion__header" (click)="toggleBlock('standard')">
            <span class="ptl-accordion__chevron" [class.is-open]="openBlocks().standard">›</span>
            {{ 'portTariffLines.blocks.standard' | translate }}
            <span class="ptl-accordion__rate">{{ blockDisplayRate(data.lineType, data.unitType, data.rateValue) }}</span>
          </button>

          @if (openBlocks().standard) {
            <div class="ptl-formula">
              <p class="ptl-formula__title">{{ formulaTitleKey(data.lineType) | translate }}</p>
              <div class="ptl-formula__panel">
                <div class="ptl-formula__row">
                  <span class="ptl-formula__prefix">{{ isPercentRate(data.lineType, data.unitType) ? '%' : '$' }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="rateValueFieldConfig()"
                    [value]="data.rateValue"
                    [mode]="mode"
                    [formValue]="data"
                    (valueChange)="updateField('rateValue', $event)">
                  </div>
                  <span class="ptl-formula__sep">×</span>
                  <div
                    class="ptl-formula__unit"
                    coreDynamicField
                    [field]="unitTypeFieldConfig()"
                    [value]="data.unitType"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="setUnitType(updateField, $event)">
                  </div>
                </div>

                @if (needsVesselPropertySelector(data.lineType, data.unitType)) {
                  <div
                    coreDynamicField
                    [field]="vesselPropertyFieldConfig()"
                    [value]="data.vesselProperty"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="updateField('vesselProperty', $event)">
                  </div>
                }

                @if (needsCargoTypeSelector(data.unitType)) {
                  <div
                    coreDynamicField
                    [field]="cargoTypeFieldConfig()"
                    [value]="cargoTypeSelectValue(data.cargoType)"
                    [mode]="mode"
                    [formValue]="data"
                    (valueChange)="onCargoTypeChange(updateField, 'cargoType', $event)">
                  </div>
                }

                <div class="ptl-formula__row">
                  <span class="ptl-formula__sep">×</span>
                  <span class="ptl-formula__prefix">{{ 'portTariffLines.fields.quantityMultiplier' | translate }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="quantityMultiplierFieldConfig()"
                    [value]="data.quantityMultiplier"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="updateField('quantityMultiplier', $event)">
                  </div>
                </div>

                <p class="ptl-formula__preview">
                  <em>{{ 'portTariffLines.formula.previewLabel' | translate }}: {{ blockPreview(data.lineType, data.unitType, data.vesselProperty, data.rateValue, data.quantityMultiplier) }}</em>
                </p>
              </div>
            </div>
          }
        </div>

        <div class="ptl-accordion__section">
          <button type="button" class="ptl-accordion__header" (click)="toggleBlock('ot1')">
            <span class="ptl-accordion__chevron" [class.is-open]="openBlocks().ot1">›</span>
            {{ 'portTariffLines.blocks.ot1' | translate }}
            <span class="ptl-accordion__rate">{{ blockDisplayRate(data.lineType, data.ot1UnitType, data.ot1RateValue) }}</span>
          </button>

          @if (openBlocks().ot1) {
            <div class="ptl-formula">
              <div class="ptl-formula__panel">
                <button type="button" class="ptl-copy-btn" (click)="copyStandardToBlock(updateField, data, 'ot1')">
                  {{ 'portTariffLines.actions.copyFromStandard' | translate }}
                </button>

                <div class="ptl-formula__row">
                  <span class="ptl-formula__prefix">{{ isPercentRate(data.lineType, data.ot1UnitType) ? '%' : '$' }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="ot1RateValueFieldConfig()"
                    [value]="data.ot1RateValue"
                    [mode]="mode"
                    (valueChange)="updateField('ot1RateValue', $event)">
                  </div>
                  <span class="ptl-formula__sep">×</span>
                  <div
                    class="ptl-formula__unit"
                    coreDynamicField
                    [field]="ot1UnitTypeFieldConfig()"
                    [value]="data.ot1UnitType"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="setOt1UnitType(updateField, $event)">
                  </div>
                </div>

                @if (needsVesselPropertySelector(data.lineType, data.ot1UnitType)) {
                  <div
                    coreDynamicField
                    [field]="ot1VesselPropertyFieldConfig()"
                    [value]="data.ot1VesselProperty"
                    [mode]="mode"
                    [rowData]="data"
                    (valueChange)="updateField('ot1VesselProperty', $event)">
                  </div>
                }

                @if (needsCargoTypeSelector(data.ot1UnitType)) {
                  <div
                    coreDynamicField
                    [field]="ot1CargoTypeFieldConfig()"
                    [value]="cargoTypeSelectValue(data.ot1CargoType)"
                    [mode]="mode"
                    (valueChange)="onCargoTypeChange(updateField, 'ot1CargoType', $event)">
                  </div>
                }

                <div class="ptl-formula__row">
                  <span class="ptl-formula__sep">×</span>
                  <span class="ptl-formula__prefix">{{ 'portTariffLines.fields.quantityMultiplier' | translate }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="ot1QuantityMultiplierFieldConfig()"
                    [value]="data.ot1QuantityMultiplier"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="updateField('ot1QuantityMultiplier', $event)">
                  </div>
                </div>

                <p class="ptl-formula__preview">
                  <em>{{ 'portTariffLines.formula.previewLabel' | translate }}: {{ blockPreview(data.lineType, data.ot1UnitType, data.ot1VesselProperty, data.ot1RateValue, data.ot1QuantityMultiplier) }}</em>
                </p>
              </div>
            </div>
          }
        </div>

        <div class="ptl-accordion__section">
          <button type="button" class="ptl-accordion__header" (click)="toggleBlock('ot2')">
            <span class="ptl-accordion__chevron" [class.is-open]="openBlocks().ot2">›</span>
            {{ 'portTariffLines.blocks.ot2' | translate }}
            <span class="ptl-accordion__rate">{{ blockDisplayRate(data.lineType, data.ot2UnitType, data.ot2RateValue) }}</span>
          </button>

          @if (openBlocks().ot2) {
            <div class="ptl-formula">
              <div class="ptl-formula__panel">
                <button type="button" class="ptl-copy-btn" (click)="copyStandardToBlock(updateField, data, 'ot2')">
                  {{ 'portTariffLines.actions.copyFromStandard' | translate }}
                </button>

                <div class="ptl-formula__row">
                  <span class="ptl-formula__prefix">{{ isPercentRate(data.lineType, data.ot2UnitType) ? '%' : '$' }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="ot2RateValueFieldConfig()"
                    [value]="data.ot2RateValue"
                    [mode]="mode"
                    (valueChange)="updateField('ot2RateValue', $event)">
                  </div>
                  <span class="ptl-formula__sep">×</span>
                  <div
                    class="ptl-formula__unit"
                    coreDynamicField
                    [field]="ot2UnitTypeFieldConfig()"
                    [value]="data.ot2UnitType"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="setOt2UnitType(updateField, $event)">
                  </div>
                </div>

                @if (needsVesselPropertySelector(data.lineType, data.ot2UnitType)) {
                  <div
                    coreDynamicField
                    [field]="ot2VesselPropertyFieldConfig()"
                    [value]="data.ot2VesselProperty"
                    [mode]="mode"
                    [rowData]="data"
                    (valueChange)="updateField('ot2VesselProperty', $event)">
                  </div>
                }

                @if (needsCargoTypeSelector(data.ot2UnitType)) {
                  <div
                    coreDynamicField
                    [field]="ot2CargoTypeFieldConfig()"
                    [value]="cargoTypeSelectValue(data.ot2CargoType)"
                    [mode]="mode"
                    (valueChange)="onCargoTypeChange(updateField, 'ot2CargoType', $event)">
                  </div>
                }

                <div class="ptl-formula__row">
                  <span class="ptl-formula__sep">×</span>
                  <span class="ptl-formula__prefix">{{ 'portTariffLines.fields.quantityMultiplier' | translate }}</span>
                  <div
                    class="ptl-formula__rate"
                    coreDynamicField
                    [field]="ot2QuantityMultiplierFieldConfig()"
                    [value]="data.ot2QuantityMultiplier"
                    [mode]="mode"
                    [rowData]="data"
                    [formValue]="data"
                    (valueChange)="updateField('ot2QuantityMultiplier', $event)">
                  </div>
                </div>

                <p class="ptl-formula__preview">
                  <em>{{ 'portTariffLines.formula.previewLabel' | translate }}: {{ blockPreview(data.lineType, data.ot2UnitType, data.ot2VesselProperty, data.ot2RateValue, data.ot2QuantityMultiplier) }}</em>
                </p>
              </div>
            </div>
          }
        </div>
      </div>

    </div>
  }
</ng-template>

<core-generic-modal
  [isOpen]="lineFormOpen()"
  [title]="editingLineId() ? ('portTariffLines.modals.editTitle' | translate) : ('portTariffLines.modals.addTitle' | translate)"
  [mode]="lineModalMode()"
  [data]="lineDraft"
  [fields]="lineModalFields()"
  [customTemplate]="lineFormTemplate"
  (save)="onLineModalSave($event)"
  (close)="closeLineForm()">
</core-generic-modal>
